// src/pages/signagepage.js
// Digital Signage Campaign Manager — "Carteleria Digital"
import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Tag, Modal as AntModal, message } from 'antd';
import {
    PlayCircleOutlined,
    CalendarOutlined,
    AppstoreOutlined,
    PlusOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { BsFileEarmarkPlayFill, BsPlayCircle, BsStopCircle } from 'react-icons/bs';
import { MdEdit, MdDelete, MdVisibility } from 'react-icons/md';

import CampaignModal from '../components/signage/CampaignModal';
import CampaignPlayer from '../components/signage/CampaignPlayer';
import CampaignPreview from '../components/signage/CampaignPreview';
import { AuthImage, AuthVideo } from '../components/signage/AuthMedia';
import {
    getAllCampaigns,
    getCampaignById,
    deleteCampaign,
    setActiveCampaign,
    getActiveCampaignId,
    clearActiveCampaign,
    isCampaignScheduledNow,
} from '../services/signageService';

import './signagepage.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
Alert.displayName = 'SignagePageAlert';

const DAY_LABELS = {
    lunes: 'Lun', martes: 'Mar', miercoles: 'Mié',
    jueves: 'Jue', viernes: 'Vie', sabado: 'Sáb', domingo: 'Dom',
};

const ALL_DAYS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

function SignagePage() {
    const [campaigns, setCampaigns] = useState([]);
    const [activeCampaignId, setActiveCampaignIdState] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [playingCampaign, setPlayingCampaign] = useState(null);
    const [previewCampaign, setPreviewCampaign] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    // ── Load campaigns ──
    // Note: GET /signage/campaigns returns summary data (no slides array).
    // Full campaign (with slides) is fetched on demand via getCampaignById.
    const [activeCampaignFull, setActiveCampaignFull] = useState(null);

    const loadCampaigns = useCallback(async () => {
        const list = await getAllCampaigns();
        const activeId = await getActiveCampaignId();
        setActiveCampaignIdState(activeId);

        // Fetch full details (with slides) for all campaigns in parallel
        const fullCampaigns = await Promise.all(
            list.map(async (c) => {
                try {
                    const full = await getCampaignById(c.id);
                    return full || c;
                } catch { return c; }
            })
        );
        setCampaigns(fullCampaigns);

        // Set active campaign full data
        if (activeId) {
            const activeFull = fullCampaigns.find(c => c.id === activeId) || null;
            setActiveCampaignFull(activeFull);
        } else {
            setActiveCampaignFull(null);
        }
    }, []);

    useEffect(() => {
        loadCampaigns();
    }, [loadCampaigns]);

    // ── CRUD Handlers ──
    const handleSaveCampaign = useCallback(async () => {
        try {
            showSnackbar('Campaña guardada', 'success');
            setIsModalOpen(false);
            setEditingCampaign(null);
            await loadCampaigns();
        } catch (err) {
            showSnackbar('Error al guardar: ' + err.message, 'error');
        }
    }, [loadCampaigns, showSnackbar]);

    const handleDelete = useCallback((campaign) => {
        AntModal.confirm({
            title: '¿Eliminar campaña?',
            icon: <ExclamationCircleOutlined />,
            content: `Se eliminará "${campaign.name}" permanentemente.`,
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                await deleteCampaign(campaign.id);
                showSnackbar('Campaña eliminada', 'info');
                await loadCampaigns();
            },
        });
    }, [loadCampaigns, showSnackbar]);

    const handleApply = useCallback(async (campaignId) => {
        try {
            await setActiveCampaign(campaignId);
            setActiveCampaignIdState(campaignId);
            showSnackbar('Campaña aplicada — ahora activa', 'success');
        } catch (err) {
            showSnackbar('Error al activar: ' + (err.response?.data?.error || err.message), 'error');
        }
    }, [showSnackbar]);

    const handleDeactivate = useCallback(async () => {
        try {
            await clearActiveCampaign();
            setActiveCampaignIdState(null);
            showSnackbar('Campaña desactivada', 'info');
        } catch (err) {
            showSnackbar('Error al desactivar: ' + (err.response?.data?.error || err.message), 'error');
        }
    }, [showSnackbar]);

    const handleEdit = useCallback(async (campaign) => {
        try {
            const full = await getCampaignById(campaign.id);
            setEditingCampaign(full || campaign);
            setIsModalOpen(true);
        } catch {
            setEditingCampaign(campaign);
            setIsModalOpen(true);
        }
    }, []);

    const handleCreate = useCallback(() => {
        if (isDemoMode) { message.info('Nueva Campaña — disponible en la versión completa del sistema'); return; }
        setEditingCampaign(null);
        setIsModalOpen(true);
    }, []);

    const handlePlay = useCallback(async (campaign) => {
        try {
            const full = await getCampaignById(campaign.id);
            setPlayingCampaign(full || campaign);
        } catch {
            setPlayingCampaign(campaign);
        }
    }, []);

    const handleExitPlayer = useCallback(() => {
        setPlayingCampaign(null);
    }, []);

    // ── Derived data ──
    const activeCampaign = campaigns.find(c => c.id === activeCampaignId) || null;

    // ── Calendar data ──
    const getScheduleForDay = useCallback((dayKey) => {
        return campaigns.filter(c => {
            if (c.id !== activeCampaignId) return false;
            const days = c.schedule?.days || [];
            return days.length === 0 || days.includes(dayKey);
        });
    }, [campaigns, activeCampaignId]);

    const today = new Date();
    const todayDayKey = ALL_DAYS[today.getDay() === 0 ? 6 : today.getDay() - 1];

    // ── Tab: Active / Overview ──
    const renderOverview = () => (
        <div>
            {/* Active Campaign Banner */}
            {activeCampaign ? (
                <div className="signage-active-banner">
                    <div className="signage-active-banner-info">
                        <div className="active-dot" />
                        <div>
                            <strong style={{ fontSize: '1rem' }}>{activeCampaign.name}</strong>
                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                {activeCampaign.description || 'Sin descripción'}
                                {' — '}
                                {activeCampaign.slides?.length || activeCampaign.slidesCount || 0} slides
                                {isCampaignScheduledNow(activeCampaign)
                                    ? <Tag color="green" style={{ marginLeft: 8 }}>En horario</Tag>
                                    : <Tag color="orange" style={{ marginLeft: 8 }}>Fuera de horario</Tag>
                                }
                            </div>
                        </div>
                    </div>
                    <div className="signage-active-banner-actions">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<BsPlayCircle />}
                            onClick={() => handlePlay(activeCampaign)}
                            sx={{ textTransform: 'none' }}
                        >
                            Reproducir
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={<BsStopCircle />}
                            onClick={handleDeactivate}
                            sx={{ textTransform: 'none' }}
                        >
                            Desactivar
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="signage-active-banner" style={{ borderColor: '#d9d9d9', background: '#fafafa' }}>
                    <div className="signage-active-banner-info">
                        <span style={{ color: '#999' }}>No hay campaña activa. Creá o aplicá una campaña para comenzar.</span>
                    </div>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<PlusOutlined />}
                        onClick={handleCreate}
                        sx={{ textTransform: 'none' }}
                    >
                        Nueva Campaña
                    </Button>
                </div>
            )}

            {/* Mini Preview */}
            {activeCampaignFull && (
                <div style={{ margin: '16px 0' }}>
                    <CampaignPreview campaign={activeCampaignFull} />
                </div>
            )}

            {/* Thumbnails of active campaign */}
            {activeCampaignFull && activeCampaignFull.slides?.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                    {activeCampaignFull.slides.map((slide, i) => (
                        <AuthImage
                            key={slide.id}
                            src={slide.thumbnail || slide.src}
                            alt={slide.filename}
                            style={{
                                width: 100, height: 70, objectFit: 'cover',
                                borderRadius: 6, border: '1px solid #e8e8e8'
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );

    // ── Tab: Calendar ──
    const renderCalendar = () => (
        <div>
            <h3 style={{ marginBottom: 12 }}>Programación Semanal</h3>
            <div className="signage-calendar-summary">
                {ALL_DAYS.map(dayKey => {
                    const booked = getScheduleForDay(dayKey);
                    const isToday = dayKey === todayDayKey;
                    return (
                        <div
                            key={dayKey}
                            className={`signage-day-cell ${booked.length > 0 ? 'has-campaign' : ''} ${isToday ? 'today' : ''}`}
                        >
                            <div className="day-name">
                                {DAY_LABELS[dayKey]}
                                {isToday && <Tag color="green" style={{ marginLeft: 4, fontSize: 10 }}>HOY</Tag>}
                            </div>
                            {booked.length > 0 ? (
                                booked.map(c => (
                                    <div className="day-campaign" key={c.id}>{c.name}</div>
                                ))
                            ) : (
                                <div style={{ fontSize: '0.75rem', color: '#ccc' }}>—</div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // ── Tab: All Campaigns ──
    const renderAllCampaigns = () => (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Campañas Guardadas ({campaigns.length})</h3>
                <Button
                    variant="contained"
                    startIcon={<PlusOutlined />}
                    onClick={handleCreate}
                    sx={{ textTransform: 'none' }}
                >
                    Nueva Campaña
                </Button>
            </div>

            {campaigns.length === 0 ? (
                <div className="signage-empty-state">
                    <BsFileEarmarkPlayFill className="empty-icon" />
                    <h3>No hay campañas</h3>
                    <p>Creá tu primera campaña de cartelería digital.</p>
                    <Button variant="contained" startIcon={<PlusOutlined />} onClick={handleCreate} sx={{ textTransform: 'none' }}>
                        Crear Campaña
                    </Button>
                </div>
            ) : (
                <div className="signage-campaigns-grid">
                    {campaigns.map(campaign => (
                        <div
                            key={campaign.id}
                            className={`signage-campaign-card ${campaign.id === activeCampaignId ? 'active' : ''}`}
                        >
                            {/* Thumbnails strip */}
                            <div className="signage-campaign-card-thumbnails">
                                {(campaign.slides || []).slice(0, 5).map(slide => (
                                    slide.type === 'video' ? (
                                        <AuthVideo key={slide.id} src={slide.src} muted style={{ pointerEvents: 'none' }} />
                                    ) : (
                                        <AuthImage key={slide.id} src={slide.thumbnail || slide.src} alt={slide.filename} />
                                    )
                                ))}
                                {(!campaign.slides || campaign.slides.length === 0) && (
                                    <div style={{
                                        flex: 1, display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', color: '#666'
                                    }}>
                                        Sin slides
                                    </div>
                                )}
                            </div>

                            <div className="signage-campaign-card-body">
                                <h4>
                                    {campaign.name}
                                    {campaign.id === activeCampaignId && (
                                        <Tag color="green" style={{ fontSize: 10 }}>ACTIVA</Tag>
                                    )}
                                </h4>
                                <p>{campaign.description || 'Sin descripción'}</p>

                                <div className="signage-campaign-card-meta">
                                    <Tag>{campaign.orientation === 'portrait' ? 'Vertical' : 'Horizontal'}</Tag>
                                    <Tag>{campaign.slides?.length || campaign.slidesCount || 0} slides</Tag>
                                    {campaign.schedule?.days?.length > 0 ? (
                                        <Tag color="blue">
                                            {campaign.schedule.days.map(d => DAY_LABELS[d]).join(', ')}
                                        </Tag>
                                    ) : (
                                        <Tag color="blue">Todos los días</Tag>
                                    )}
                                </div>

                                <div className="signage-campaign-card-actions">
                                    {campaign.id !== activeCampaignId ? (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleApply(campaign.id)}
                                            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                                        >
                                            Aplicar
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="error"
                                            onClick={handleDeactivate}
                                            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                                        >
                                            Desactivar
                                        </Button>
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={() => handlePlay(campaign)}
                                        title="Reproducir"
                                    >
                                        <BsPlayCircle />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={async () => {
                                            try {
                                                const full = await getCampaignById(campaign.id);
                                                setPreviewCampaign(full || campaign);
                                            } catch { setPreviewCampaign(campaign); }
                                        }}
                                        title="Vista Previa"
                                    >
                                        <MdVisibility />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleEdit(campaign)}
                                        title="Editar"
                                    >
                                        <MdEdit />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDelete(campaign)}
                                        title="Eliminar"
                                        color="error"
                                        disabled={isDemoMode}
                                    >
                                        <MdDelete />
                                    </IconButton>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ── Tab items ──
    const tabItems = [
        {
            key: '1',
            label: <span><PlayCircleOutlined /> Campaña Activa</span>,
            children: renderOverview(),
        },
        {
            key: '2',
            label: <span><CalendarOutlined /> Calendario</span>,
            children: renderCalendar(),
        },
        {
            key: '3',
            label: <span><AppstoreOutlined /> Todas las Campañas</span>,
            children: renderAllCampaigns(),
        },
    ];

    return (
        <>
            <div className="signage-page">
                <div className="signage-page-header">
                    <BsFileEarmarkPlayFill size={36} style={{ color: '#333' }} />
                    <h2>CARTELERÍA DIGITAL</h2>
                </div>

                <Tabs
                    defaultActiveKey="1"
                    items={tabItems}
                    className="signage-tabs"
                />
            </div>

            {/* Create / Edit Modal */}
            <CampaignModal
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingCampaign(null); }}
                onSaved={handleSaveCampaign}
                editCampaign={editingCampaign}
                allCampaigns={campaigns}
            />

            {/* Fullscreen Player */}
            {playingCampaign && (
                <CampaignPlayer
                    campaign={playingCampaign}
                    onExit={handleExitPlayer}
                    loop={true}
                />
            )}

            {/* Preview Modal (inline) */}
            {previewCampaign && (
                <AntModal
                    open={true}
                    title={`Vista Previa: ${previewCampaign.name}`}
                    onCancel={() => setPreviewCampaign(null)}
                    footer={null}
                    width={previewCampaign.orientation === 'portrait' ? 380 : 560}
                    centered
                >
                    <CampaignPreview campaign={previewCampaign} />
                </AntModal>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default SignagePage;